import React, { useEffect, useState, useCallback } from 'react';
import { Link, Navigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import api from '@/lib/api';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Building2, Users, Ticket as TicketIcon, AlertTriangle, CheckCircle2, Clock3, TrendingUp, Activity, Calendar, UserCheck, Package, Plus, LayoutDashboard, Trash2 } from 'lucide-react';
import { StatusBadge, PriorityBadge } from '@/components/Badges';
import { TICKET_STATUS_LABEL, PRIORITY_LABEL } from '@/lib/labels';
import { formatRelative } from '@/lib/format';
import { LoadingState } from '@/components/States';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, Legend } from 'recharts';
import DashboardEditor from '@/components/dashboard/DashboardEditor';
import WidgetLibrary from '@/components/dashboard/WidgetLibrary';
import WidgetCustomizer from '@/components/dashboard/WidgetCustomizer';
import Modal from 'react-modal';
import { toast } from 'sonner';

Modal.setAppElement('#root');

const PIE_COLORS = ['#2563EB', '#10B981', '#F59E0B', '#EF4444', '#6366F1', '#8B5CF6', '#06B6D4', '#84CC16'];

function StatCard({ icon: Icon, label, value, color = 'blue', hint }) {
  const palette = {
    blue: 'bg-blue-50 text-blue-600',
    green: 'bg-emerald-50 text-emerald-600',
    amber: 'bg-amber-50 text-amber-600',
    red: 'bg-red-50 text-red-600',
    indigo: 'bg-indigo-50 text-indigo-600',
    slate: 'bg-slate-50 text-slate-600',
  };
  return (
    <Card className="p-5 border-slate-200">
      <div className="flex items-start justify-between">
        <div>
          <div className="text-sm text-slate-500">{label}</div>
          <div className="text-2xl font-bold text-slate-900 mt-1">{value ?? '-'}</div>
          {hint && <div className="text-xs text-slate-400 mt-1">{hint}</div>}
        </div>
        <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${palette[color]}`}>
          <Icon className="w-5 h-5" />
        </div>
      </div>
    </Card>
  );
}

export default function DashboardPage() {
  const { user, hasRole } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dashboards, setDashboards] = useState([]);
  const [selectedDashboard, setSelectedDashboard] = useState('default');
  const [customDashboard, setCustomDashboard] = useState(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showWidgetTypeModal, setShowWidgetTypeModal] = useState(false);
  const [showWidgetLibrary, setShowWidgetLibrary] = useState(false);
  const [showWidgetCustomizer, setShowWidgetCustomizer] = useState(false);
  const [newDashboard, setNewDashboard] = useState({ name: '', description: '', isShared: false });
  
  // Período padrão: mês atual
  const today = new Date();
  const firstDay = new Date(today.getFullYear(), today.getMonth(), 1);
  const lastDay = new Date(today.getFullYear(), today.getMonth() + 1, 0);
  
  const [startDate, setStartDate] = useState(firstDay.toISOString().split('T')[0]);
  const [endDate, setEndDate] = useState(lastDay.toISOString().split('T')[0]);

  const loadDashboard = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (startDate) params.startDate = startDate;
      if (endDate) params.endDate = endDate;
      
      const { data } = await api.get('/reports/dashboard', { params });
      setData(data);
    } finally {
      setLoading(false);
    }
  }, [startDate, endDate]);

  useEffect(() => {
    loadDashboard();
    if (hasRole('SUPER_ADMIN') || hasRole('ADMIN') || hasRole('SUPERVISOR')) {
      loadCustomDashboards();
    }
  }, [loadDashboard]);

  useEffect(() => {
    if (selectedDashboard !== 'default' && selectedDashboard) {
      loadCustomDashboard(selectedDashboard);
    }
  }, [selectedDashboard]);

  const loadCustomDashboards = async () => {
    try {
      const { data } = await api.get('/dashboards');
      setDashboards(data);
    } catch (error) {
      console.error('Erro ao carregar dashboards:', error);
    }
  };

  const loadCustomDashboard = async (id) => {
    try {
      const { data } = await api.get(`/dashboards/${id}`);
      setCustomDashboard(data);
    } catch (error) {
      console.error('Erro ao carregar dashboard:', error);
      toast.error('Erro ao carregar dashboard');
    }
  };

  const handleCreateDashboard = async () => {
    if (!newDashboard.name.trim()) {
      toast.error('Nome do dashboard é obrigatório');
      return;
    }
    try {
      const { data } = await api.post('/dashboards', newDashboard);
      setDashboards([...dashboards, data]);
      setSelectedDashboard(data.id);
      setShowCreateModal(false);
      setNewDashboard({ name: '', description: '', isShared: false });
      toast.success('Dashboard criado com sucesso')
    } catch (error) {
      console.error('Erro ao criar dashboard:', error);
      toast.error('Erro ao criar dashboard');
    }
  };

  const handleDeleteDashboard = async () => {
    if (!customDashboard) return;
    
    const confirmed = window.confirm(
      `Confirma exclusão do Dashboard "${customDashboard.name}"?\n\nEsta ação não pode ser desfeita.`
    );
    
    if (!confirmed) return;
    
    try {
      await api.delete(`/dashboards/${customDashboard.id}`);
      const updatedDashboards = dashboards.filter(d => d.id !== customDashboard.id);
      setDashboards(updatedDashboards);
      setSelectedDashboard('default');
      setCustomDashboard(null);
      toast.success('Dashboard deletado com sucesso');
    } catch (error) {
      console.error('Erro ao deletar dashboard:', error);
      toast.error('Erro ao deletar dashboard');
    }
  };

  const handleAddWidget = async (widgetTemplate) => {
    if (!customDashboard) return;
    try {
      await api.post(`/dashboards/${customDashboard.id}/widgets`, {
        type: widgetTemplate.type,
        title: widgetTemplate.title,
        dataSource: widgetTemplate.dataSource,
        config: widgetTemplate.config,
        position: widgetTemplate.defaultPosition,
      });
      loadCustomDashboard(customDashboard.id);
      toast.success('Widget adicionado');
    } catch (error) {
      console.error('Erro ao adicionar widget:', error);
      toast.error('Erro ao adicionar widget');
    }
  };

  const handleUpdateDashboard = () => {
    if (customDashboard) {
      loadCustomDashboard(customDashboard.id);
    }
  };

  // Atendente não deve ver dashboard
  if (hasRole('ATTENDANT') && !hasRole('ADMIN') && !hasRole('SUPERVISOR')) {
    return <Navigate to="/tickets" replace />;
  }

  if (loading) return <LoadingState rows={8} />;
  if (!data) return <div className="text-slate-500">Sem dados</div>;

  const isSA = hasRole('SUPER_ADMIN');
  const roleType = (user?.roles || [])[0]?.type;

  const byStatus = (data.byStatus || []).map((s) => ({ name: TICKET_STATUS_LABEL[s.status] || s.status, qtd: s.count }));
  const byPriority = (data.byPriority || []).map((s) => ({ name: PRIORITY_LABEL[s.priority] || s.priority, value: s.count }));
  const byCategory = (data.byCategory || []).map((s) => ({ name: s.categoryName, value: s.count }));
  const byProduct = (data.byProduct || []).map((s) => ({ name: s.productName, value: s.count }));

  const canCreateDashboard = hasRole('SUPER_ADMIN') || hasRole('ADMIN') || hasRole('SUPERVISOR');

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div className="flex items-center gap-4">
          <div>
            <h1 className="text-2xl font-semibold text-slate-900">Dashboard</h1>
            <p className="text-sm text-slate-500">Olá, {user?.name?.split(' ')[0]}! Aqui está o resumo da sua operação.</p>
          </div>
          {canCreateDashboard && (
            <div className="flex items-center gap-2">
              <Select value={selectedDashboard} onValueChange={setSelectedDashboard}>
                <SelectTrigger className="w-48">
                  <SelectValue placeholder="Selecione um dashboard" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="default">
                    <div className="flex items-center gap-2">
                      <LayoutDashboard className="w-4 h-4" />
                      Dashboard Padrão
                    </div>
                  </SelectItem>
                  {dashboards.map(d => (
                    <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Button onClick={() => setShowCreateModal(true)} size="sm" className="bg-blue-600 hover:bg-blue-700">
                <Plus className="w-4 h-4" />
              </Button>
            </div>
          )}
        </div>
        <div className="flex items-end gap-2">
          {selectedDashboard === 'default' && (
            <>
              <div>
                <Label className="text-xs text-slate-600">Data Inicial</Label>
                <Input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} className="w-40" />
              </div>
              <div>
                <Label className="text-xs text-slate-600">Data Final</Label>
                <Input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} className="w-40" />
              </div>
              <Button onClick={loadDashboard} className="bg-blue-600 hover:bg-blue-700">
                <Calendar className="w-4 h-4 mr-2" /> Filtrar
              </Button>
            </>
          )}
          {selectedDashboard !== 'default' && customDashboard && (
            <div className="flex items-center gap-2">
              <Button onClick={handleDeleteDashboard} variant="outline" className="border-red-200 text-red-600 hover:bg-red-50">
                <Trash2 className="w-4 h-4 mr-2" /> Deletar Dashboard
              </Button>
              <Button onClick={() => setShowWidgetTypeModal(true)} className="bg-blue-600 hover:bg-blue-700">
                <Plus className="w-4 h-4 mr-2" /> Adicionar Widget
              </Button>
            </div>
          )}
        </div>
      </div>

      {/* Dashboard Personalizado */}
      {selectedDashboard !== 'default' && customDashboard ? (
        <DashboardEditor
          dashboard={customDashboard}
          onUpdate={handleUpdateDashboard}
          onAddWidget={() => setShowWidgetLibrary(true)}
        />
      ) : (
        <>
      {/* Dashboard Padrão */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {isSA ? (
          <>
            <StatCard icon={Building2} label="Empresas Ativas" value={data.activeCompanies} color="blue" hint={`${data.totalCompanies} no total`} />
            <StatCard icon={Users} label="Usuários" value={data.totalUsers} color="indigo" />
            <StatCard icon={UserCheck} label="Clientes" value={data.totalCustomers} color="green" />
            <StatCard icon={TicketIcon} label="Tickets" value={data.totalTickets} color="slate" hint={`${data.openTickets} abertos`} />
          </>
        ) : roleType === 'CLIENT' ? (
          <>
            <StatCard icon={TicketIcon} label="Meus Tickets" value={data.totalTickets} color="blue" />
            <StatCard icon={Activity} label="Em andamento" value={data.openTickets} color="amber" />
            <StatCard icon={CheckCircle2} label="Finalizados" value={data.finalizedTickets} color="green" />
            <StatCard icon={AlertTriangle} label="SLA em risco" value={data.slaAtRisk} color="red" />
          </>
        ) : (
          <>
            <StatCard icon={TicketIcon} label="Tickets" value={data.totalTickets} color="blue" />
            <StatCard icon={Activity} label="Abertos" value={data.openTickets} color="amber" />
            <StatCard icon={CheckCircle2} label="Finalizados" value={data.finalizedTickets} color="green" />
            <StatCard icon={Users} label="Clientes" value={data.totalCustomers} color="indigo" hint={`${data.totalProducts} produtos`} />
          </>
        )}
      </div>

      {/* Cards de SLA */}
      {data.slaStats && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Card className="p-5 border-emerald-200 bg-emerald-50/30">
            <div className="flex items-start justify-between">
              <div>
                <div className="text-sm text-emerald-700 font-medium">🟢 Dentro do Prazo</div>
                <div className="text-2xl font-bold text-emerald-900 mt-1">{data.slaStats.onTime}</div>
                <div className="text-xs text-emerald-600 mt-1">Mais de 3 dias restantes</div>
              </div>
              <div className="w-10 h-10 rounded-xl flex items-center justify-center bg-emerald-100">
                <CheckCircle2 className="w-5 h-5 text-emerald-600" />
              </div>
            </div>
          </Card>
          
          <Card className="p-5 border-amber-200 bg-amber-50/30">
            <div className="flex items-start justify-between">
              <div>
                <div className="text-sm text-amber-700 font-medium">🟡 Vencendo</div>
                <div className="text-2xl font-bold text-amber-900 mt-1">{data.slaStats.atRisk}</div>
                <div className="text-xs text-amber-600 mt-1">1 a 3 dias para vencer</div>
              </div>
              <div className="w-10 h-10 rounded-xl flex items-center justify-center bg-amber-100">
                <Clock3 className="w-5 h-5 text-amber-600" />
              </div>
            </div>
          </Card>
          
          <Card className="p-5 border-red-200 bg-red-50/30">
            <div className="flex items-start justify-between">
              <div>
                <div className="text-sm text-red-700 font-medium">🔴 Vencidos</div>
                <div className="text-2xl font-bold text-red-900 mt-1">{data.slaStats.overdue}</div>
                <div className="text-xs text-red-600 mt-1">SLA excedido</div>
              </div>
              <div className="w-10 h-10 rounded-xl flex items-center justify-center bg-red-100">
                <AlertTriangle className="w-5 h-5 text-red-600" />
              </div>
            </div>
          </Card>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <Card className="p-5 lg:col-span-2 border-slate-200">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h3 className="text-base font-semibold text-slate-900">Tickets por Status</h3>
              <p className="text-xs text-slate-500">Distribuição no período</p>
            </div>
            <TrendingUp className="w-4 h-4 text-slate-400" />
          </div>
          {byStatus.length === 0 ? (
            <div className="text-sm text-slate-400 py-12 text-center">Sem dados</div>
          ) : (
            <ResponsiveContainer width="100%" height={260}>
              <BarChart data={byStatus}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                <XAxis dataKey="name" tick={{ fontSize: 12 }} />
                <YAxis allowDecimals={false} tick={{ fontSize: 12 }} />
                <Tooltip />
                <Bar dataKey="qtd" fill="#2563EB" radius={[6, 6, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          )}
        </Card>

        <Card className="p-5 border-slate-200">
          <h3 className="text-base font-semibold text-slate-900">Por Prioridade</h3>
          <p className="text-xs text-slate-500 mb-2">Tickets no período</p>
          {byPriority.length === 0 ? (
            <div className="text-sm text-slate-400 py-12 text-center">Sem dados</div>
          ) : (
            <ResponsiveContainer width="100%" height={240}>
              <PieChart>
                <Pie data={byPriority} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={70} innerRadius={40}>
                  {byPriority.map((_, i) => <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />)}
                </Pie>
                <Tooltip />
                <Legend wrapperStyle={{ fontSize: 12 }} />
              </PieChart>
            </ResponsiveContainer>
          )}
        </Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Card className="p-5 border-slate-200">
          <h3 className="text-base font-semibold text-slate-900">Por Categoria</h3>
          <p className="text-xs text-slate-500 mb-2">Tickets no período</p>
          {byCategory.length === 0 ? (
            <div className="text-sm text-slate-400 py-12 text-center">Sem dados</div>
          ) : (
            <ResponsiveContainer width="100%" height={240}>
              <PieChart>
                <Pie data={byCategory} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={70} innerRadius={40}>
                  {byCategory.map((_, i) => <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />)}
                </Pie>
                <Tooltip />
                <Legend wrapperStyle={{ fontSize: 12 }} />
              </PieChart>
            </ResponsiveContainer>
          )}
        </Card>

        <Card className="p-5 border-slate-200">
          <h3 className="text-base font-semibold text-slate-900">Por Produto</h3>
          <p className="text-xs text-slate-500 mb-2">Tickets no período</p>
          {byProduct.length === 0 ? (
            <div className="text-sm text-slate-400 py-12 text-center">Sem dados</div>
          ) : (
            <ResponsiveContainer width="100%" height={240}>
              <PieChart>
                <Pie data={byProduct} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={70} innerRadius={40}>
                  {byProduct.map((_, i) => <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />)}
                </Pie>
                <Tooltip />
                <Legend wrapperStyle={{ fontSize: 12 }} />
              </PieChart>
            </ResponsiveContainer>
          )}
        </Card>
      </div>

      {isSA && data.companiesStats && data.companiesStats.length > 0 && (
        <Card className="p-5 border-slate-200">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h3 className="text-base font-semibold text-slate-900">Estatísticas por Empresa</h3>
              <p className="text-xs text-slate-500">Top 10 empresas ativas</p>
            </div>
            <Package className="w-4 h-4 text-slate-400" />
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-slate-200">
                  <th className="text-left py-2 px-2 font-medium text-slate-700">Empresa</th>
                  <th className="text-center py-2 px-2 font-medium text-slate-700">Usuários</th>
                  <th className="text-center py-2 px-2 font-medium text-slate-700">Clientes</th>
                  <th className="text-center py-2 px-2 font-medium text-slate-700">Tickets Total</th>
                  <th className="text-center py-2 px-2 font-medium text-slate-700">Tickets Período</th>
                  <th className="text-center py-2 px-2 font-medium text-slate-700">Abertos</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {data.companiesStats.map((company) => (
                  <tr key={company.companyId} className="hover:bg-slate-50">
                    <td className="py-2 px-2">
                      <div className="font-medium text-slate-900">{company.companyName}</div>
                      <div className="text-xs text-slate-500">{company.companyCode}</div>
                    </td>
                    <td className="text-center py-2 px-2 text-slate-700">{company.totalUsers}</td>
                    <td className="text-center py-2 px-2 text-slate-700">{company.totalCustomers}</td>
                    <td className="text-center py-2 px-2 text-slate-700">{company.totalTickets}</td>
                    <td className="text-center py-2 px-2 text-slate-700">{company.ticketsInPeriod}</td>
                    <td className="text-center py-2 px-2">
                      <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-800">
                        {company.openTicketsInPeriod}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      <Card className="border-slate-200">
        <div className="flex items-center justify-between p-5 border-b border-slate-200">
          <div>
            <h3 className="text-base font-semibold text-slate-900">Tickets Recentes</h3>
            <p className="text-xs text-slate-500">Últimas atualizações</p>
          </div>
          <Link to="/tickets" className="text-sm text-blue-600 hover:underline">Ver todos</Link>
        </div>
        <div className="divide-y divide-slate-100">
          {(data.recentTickets || []).length === 0 ? (
            <div className="py-10 text-center text-sm text-slate-400">Nenhum ticket recente</div>
          ) : (
            (data.recentTickets || []).map((t) => (
              <Link to={`/tickets/${t.id}`} key={t.id} className="flex items-center gap-3 px-5 py-3 hover:bg-slate-50">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="font-mono text-sm font-semibold text-slate-900">{t.protocol}</span>
                    <StatusBadge status={t.status} />
                    <PriorityBadge priority={t.priority} />
                  </div>
                  <div className="text-xs text-slate-500 mt-1 truncate">{t.customerName} • {t.problemDescription}</div>
                </div>
                <div className="text-xs text-slate-400 whitespace-nowrap"><Clock3 className="w-3 h-3 inline mr-1" />{formatRelative(t.openedAt)}</div>
              </Link>
            ))
          )}
        </div>
      </Card>
      </>
      )}

      {/* Modal Criar Dashboard */}
      <Modal
        isOpen={showCreateModal}
        onRequestClose={() => setShowCreateModal(false)}
        className="modal-content max-w-md mx-auto mt-20 bg-white rounded-lg shadow-xl p-6"
        overlayClassName="modal-overlay fixed inset-0 bg-black bg-opacity-50 flex items-start justify-center"
      >
        <h2 className="text-xl font-semibold text-slate-900 mb-4">Criar Novo Dashboard</h2>
        
        <div className="space-y-4">
          <div>
            <Label>Nome *</Label>
            <Input
              value={newDashboard.name}
              onChange={(e) => setNewDashboard({ ...newDashboard, name: e.target.value })}
              placeholder="Ex: Dashboard de Vendas"
            />
          </div>

          <div>
            <Label>Descrição</Label>
            <Input
              value={newDashboard.description}
              onChange={(e) => setNewDashboard({ ...newDashboard, description: e.target.value })}
              placeholder="Descrição opcional"
            />
          </div>

          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              id="isShared"
              checked={newDashboard.isShared}
              onChange={(e) => setNewDashboard({ ...newDashboard, isShared: e.target.checked })}
              className="w-4 h-4"
            />
            <Label htmlFor="isShared" className="cursor-pointer">
              Compartilhar com Admins e Supervisores
            </Label>
          </div>

          <div className="flex gap-2 pt-4">
            <Button
              onClick={() => setShowCreateModal(false)}
              variant="outline"
              className="flex-1"
            >
              Cancelar
            </Button>
            <Button
              onClick={handleCreateDashboard}
              className="flex-1 bg-blue-600 hover:bg-blue-700"
            >
              Criar
            </Button>
          </div>
        </div>
      </Modal>

      {/* Modal Tipo de Widget */}
      <Modal
        isOpen={showWidgetTypeModal}
        onRequestClose={() => setShowWidgetTypeModal(false)}
        className="modal-content max-w-lg mx-auto mt-20 bg-white rounded-lg shadow-xl p-6"
        overlayClassName="modal-overlay fixed inset-0 bg-black bg-opacity-50 flex items-start justify-center"
      >
        <h2 className="text-xl font-semibold text-slate-900 mb-4">Escolha o Tipo de Widget</h2>
        <p className="text-sm text-slate-500 mb-6">Selecione como deseja adicionar o widget ao dashboard</p>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <button
            onClick={() => {
              setShowWidgetTypeModal(false);
              setShowWidgetLibrary(true);
            }}
            className="p-6 border-2 border-slate-200 rounded-lg hover:border-blue-500 hover:bg-blue-50 transition-all text-left group"
          >
            <div className="text-4xl mb-3">📚</div>
            <h3 className="font-semibold text-slate-900 mb-2 group-hover:text-blue-600">Padrão (Biblioteca)</h3>
            <p className="text-sm text-slate-500">Escolha widgets pré-configurados prontos para uso</p>
          </button>

          <button
            onClick={() => {
              setShowWidgetTypeModal(false);
              setShowWidgetCustomizer(true);
            }}
            className="p-6 border-2 border-slate-200 rounded-lg hover:border-blue-500 hover:bg-blue-50 transition-all text-left group"
          >
            <div className="text-4xl mb-3">⚙️</div>
            <h3 className="font-semibold text-slate-900 mb-2 group-hover:text-blue-600">Personalizado (Editor)</h3>
            <p className="text-sm text-slate-500">Crie widgets customizados do zero com o editor</p>
          </button>
        </div>

        <div className="mt-6">
          <Button onClick={() => setShowWidgetTypeModal(false)} variant="outline" className="w-full">
            Cancelar
          </Button>
        </div>
      </Modal>

      {/* Widget Library */}
      <WidgetLibrary
        isOpen={showWidgetLibrary}
        onClose={() => setShowWidgetLibrary(false)}
        onSelectWidget={handleAddWidget}
      />

      {/* Widget Customizer */}
      <WidgetCustomizer
        isOpen={showWidgetCustomizer}
        onClose={() => setShowWidgetCustomizer(false)}
        onCreateWidget={handleAddWidget}
      />
    </div>
  );
}
